import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Observable, tap, map, finalize, catchError, of } from 'rxjs';
import { EnvironmentUrl } from './shared/services/environment-url';

export interface JwtClaims {
  sub: string; // user ID
  email: string;
  displayName: string;
  roles: string[];
  exp: number; // expiry timestamp (seconds since epoch)
}

export interface AuthResponse {
  accessToken: string;
  expiresIn: number; // seconds until expiry
  // refresh token is set as httpOnly cookie by the server — not in response body
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private baseUrl = inject(EnvironmentUrl).urlAddress;

  // ── Access token in memory — XSS-safe ─────────────────────────────────
  private _accessToken = signal<string | null>(null);
  private _claims = signal<JwtClaims | null>(null);
  private _refreshTimer?: ReturnType<typeof setTimeout>;

  // ── Public read-only state ─────────────────────────────────────────────
  readonly accessToken = this._accessToken.asReadonly();
  readonly currentUser = this._claims.asReadonly();
  readonly isLoggedIn = computed(() => this._claims() !== null);
  readonly displayName = computed(() => this._claims()?.displayName ?? '');
  readonly userRoles = computed(() => this._claims()?.roles ?? []);

  hasRole(role: string): boolean {
    return this.userRoles().includes(role);
  }
  hasAnyRole(...roles: string[]): boolean {
    return roles.some((r) => this.userRoles().includes(r));
  }

  // ── Login ─────────────────────────────────────────────────────────────
  login(email: string, password: string): Observable<void> {
    return this.http
      .post<AuthResponse>(
        `${this.baseUrl}/api/auth/login`,
        { email, password },
        { withCredentials: true }, // receive httpOnly refresh token cookie
      )
      .pipe(
        tap((response) => this.setSession(response)),
        map(() => undefined),
      );
  }

  // ── Logout ────────────────────────────────────────────────────────────
  logout(): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}/api/auth/logout`, {}, { withCredentials: true })
      .pipe(
        finalize(() => {
          this.clearSession();
          this.router.navigate(['/login']);
        }),
        catchError(() => {
          this.clearSession(); // clear locally even if API call fails
          this.router.navigate(['/login']);
          return of(undefined);
        }),
      );
  }

  // ── Silent session restore on page refresh ────────────────────────────
  tryRestoreSession(): Promise<boolean> {
    return firstValueFrom(
      this.http
        .post<AuthResponse>(`${this.baseUrl}/api/auth/refresh`, {}, { withCredentials: true })
        .pipe(
          tap((response) => this.setSession(response)),
          map(() => true),
          catchError(() => of(false)),
        ),
    );
  }

  // ── Refresh (called by interceptor on 401) ────────────────────────────
  refreshToken(): Observable<string> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/api/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap((response) => this.setSession(response)),
        map((response) => response.accessToken),
      );
  }

  // ── Private helpers ───────────────────────────────────────────────────
  private setSession(response: AuthResponse): void {
    this._accessToken.set(response.accessToken);
    this._claims.set(this.decodeJwt(response.accessToken));
    this.scheduleRefresh(response.expiresIn);
  }

  clearSession(): void {
    this._accessToken.set(null);
    this._claims.set(null);
    if (this._refreshTimer) clearTimeout(this._refreshTimer);
  }

  private decodeJwt(token: string): JwtClaims | null {
    try {
      const [, payload] = token.split('.');
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  private scheduleRefresh(expiresInSeconds: number): void {
    if (this._refreshTimer) clearTimeout(this._refreshTimer);
    // Refresh 60s before expiry
    const refreshInMs = Math.max(0, (expiresInSeconds - 60) * 1000);
    this._refreshTimer = setTimeout(() => this.refreshToken().subscribe(), refreshInMs);
  }
}
